/**
 * One backtest run in full: where the rule - or the Backtest question - would
 * have fired, per entity, over the window it ran. Its progress while the
 * watcher is on it; its reason when it could not run.
 *
 * Everything shown was computed by a watcher running its live code (rule 4);
 * nothing here evaluates (rule 3). One entity at a time on the real chart
 * (`argus-backtest-chart.ts`): the series the rule reads, its stored lines,
 * and its episodes shaded. A mark on the strip, or a row of the table, puts
 * that entity there - a mark zoomed to its episode.
 *
 * Drawn by Backtest (`argus-backtest-room.ts`). The run is handed in; this
 * element never asks for one.
 */

import { html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import type { ArgusBacktest, ArgusBacktestResult } from '../../api/types.ts';
import { backtestHeadline, hoursWords } from './argus-backtest-panel.ts';
import { renderBacktestStrip } from './argus-backtest-strip.ts';
import { utcSeconds } from './argus-backtest-chart.ts';
import './argus-backtest-chart.ts';
import { rulesRoomOpenRule } from './argus-inbox-store.ts';
import { waitingWords } from './argus-backtest-store.ts';
import { setArgusRoom } from './argus-lens.ts';
import { windowWords } from './argus-window-pick.ts';
import { rules } from './argus-rules-store.ts';
import { StoreController } from '../../state/controller.ts';

export { windowWords };

export function stamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

@customElement('argus-backtest-result')
export class ArgusBacktestResultView extends LightElement {
  @property({ attribute: false }) run: ArgusBacktest | null = null;

  // `rules`: a rule's chosen charts are its tabs here (T3).
  private store = new StoreController(this, [rules]);

  @state() private showQuiet = false;
  /** The entity on the chart; null is the busiest. */
  @state() private chartEntity: string | null = null;
  /** A zoom around one episode, or null for the run's whole window. */
  @state() private chartFocus: { from: string; to: string } | null = null;
  private shownId: string | null = null;

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('run') && (this.run?.id ?? null) !== this.shownId) {
      this.shownId = this.run?.id ?? null;
      this.showQuiet = false;
      this.chartEntity = null;
      this.chartFocus = null;
    }
  }

  /** Put an entity on the chart - zoomed around one of its episodes, if given. */
  private showOnChart(entity: string, start?: string): void {
    this.chartEntity = entity;
    this.chartFocus = null;
    const episode = start ? this.run?.result?.episodes.find((e) => e.entity === entity && e.start === start) : null;
    if (episode) {
      const from = utcSeconds(episode.start);
      const to = utcSeconds(episode.end ?? episode.last);
      // Room either side to see what came before and after: a day, or the
      // episode's own length when that is longer.
      const pad = Math.max(86400, to - from);
      const iso = (s: number) => new Date(s * 1000).toISOString().slice(0, 19);
      this.chartFocus = { from: iso(from - pad), to: iso(to + pad) };
    }
    this.querySelector('.argus-btr-chart')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  private renderChart(run: ArgusBacktest, rows: ArgusBacktestResult['entities']): TemplateResult {
    const result = run.result!;
    const readable = rows.filter((r) => !r.error);
    const entity = this.chartEntity ?? readable[0]?.entity ?? null;
    const spec = entity ? run.chart?.[entity] ?? null : null;
    if (!entity) return html``;
    const episodes = result.episodes.filter((e) => e.entity === entity);
    const capped = result.totals.episodes > result.episodes.length;
    return html`<section class="argus-btr-section argus-btr-chart">
      <div class="argus-btr-chart-head">
        <h4>On the chart</h4>
        <select
          aria-label=${`Which ${result.entity_noun}`}
          @change=${(e: Event) => this.showOnChart((e.target as HTMLSelectElement).value)}
        >
          ${readable.map(
            (r) => html`<option value=${r.entity} ?selected=${r.entity === entity}>
              ${r.name}${r.episodes ? ` · ${r.episodes}` : ''}
            </option>`,
          )}
        </select>
        ${this.chartFocus
          ? html`<button type="button" class="argus-lmore" @click=${() => (this.chartFocus = null)}>
              Whole window
            </button>`
          : nothing}
      </div>
      ${run.chart === null || run.chart === undefined
        ? html`<p class="argus-lfaint">This run's rule cannot be read back, so there is nothing to draw.</p>`
        : html`<argus-backtest-chart
            .spec=${spec}
            .episodes=${episodes}
            .entity=${entity}
            .from=${this.chartFocus?.from ?? result.range_from.slice(0, 19)}
            .to=${this.chartFocus?.to ?? result.range_to.slice(0, 19)}
            .source=${run.source_key}
            .ruleId=${run.rule_id ?? ''}
            .charts=${(run.rule_id && rules.value.find((r) => r.id === run.rule_id)?.charts) || []}
          ></argus-backtest-chart>`}
      <p class="argus-lfaint">
        Shaded: where the watcher found it holding${episodes.length
          ? ` (${episodes.length} here)`
          : ' - nowhere on this one'}.
        ${spec?.lines.length ? 'Dashed: its own numbers.' : ''}
        ${capped ? `Only the newest ${result.episodes.length} episodes of the run are kept.` : ''}
      </p>
    </section>`;
  }

  private renderHead(run: ArgusBacktest): TemplateResult {
    const quick = run.purpose === 'quick';
    return html`<div class="argus-btr-head">
        <h3>${run.label || (quick ? 'Untitled question' : 'Untitled draft')}</h3>
        ${run.rule_id
          ? html`<button
              type="button"
              class="argus-lmore"
              @click=${() => {
                rulesRoomOpenRule.set(run.rule_id);
                setArgusRoom('rules');
              }}
            >
              Open the rule →
            </button>`
          : quick
            ? nothing
            : html`<span class="argus-chip is-draft">unsaved draft</span>`}
      </div>
      <p class="argus-lfaint">
        ${windowWords(run)} · asked ${stamp(run.requested_at)} by ${run.requested_by_name}${run.rule_version
          ? ` · version ${run.rule_version}`
          : ''}
      </p>`;
  }

  override render(): TemplateResult {
    const run = this.run;
    if (!run) return html``;
    const head = this.renderHead(run);
    if (run.status === 'queued' || run.status === 'running') {
      const total = run.total_entities ?? 0;
      const share = total ? Math.round((100 * run.done_entities) / total) : 0;
      return html`<div class="argus-btr-run">
        ${head}
        <div class="argus-bt-progress">
          <div class="progress-bar-track">
            <div class="progress-bar-fill ${run.status === 'queued' ? 'is-waiting' : ''}" style=${`width:${run.status === 'queued' ? 100 : share}%`}></div>
          </div>
          <span class="progress-text">${run.status === 'queued' ? waitingWords(run.ahead) : `${run.done_entities} of ${total}`}</span>
        </div>
      </div>`;
    }
    if (run.status === 'failed' || !run.result) {
      return html`<div class="argus-btr-run">${head}<p class="argus-bt-note is-error">Could not run: ${run.error ?? 'no result'}</p></div>`;
    }
    const result = run.result;
    const rows = [...result.entities].sort((a, b) => (b.episodes ?? 0) - (a.episodes ?? 0));
    const quiet = rows.filter((r) => !r.episodes && !r.error);
    const shown = this.showQuiet ? rows : rows.filter((r) => r.episodes || r.error);
    const noun = result.entity_noun.charAt(0).toUpperCase() + result.entity_noun.slice(1);
    const onChart = this.chartEntity ?? rows.find((x) => !x.error)?.entity;
    return html`<div class="argus-btr-run">
      ${head}
      <p class="argus-btr-headline">${backtestHeadline(result)}</p>
      <section class="argus-btr-section">
        <h4>Where it happened</h4>
        ${result.totals.episodes
          ? renderBacktestStrip(result, { onPick: (entity, start) => this.showOnChart(entity, start) })
          : html`<p class="argus-lfaint">Nowhere: not one episode in this window.</p>`}
      </section>
      ${this.renderChart(run, rows)}
      <section class="argus-btr-section">
        <h4>Per ${result.entity_noun}</h4>
        <table class="argus-found-table argus-bt-table">
          <thead>
            <tr>
              <th>${noun}</th>
              <th>Episodes</th>
              <th title="Time inside an episode, all of them together">In episodes</th>
              <th>Longest</th>
              <th title="Steps where the gate held and the data was there">Counted</th>
            </tr>
          </thead>
          <tbody>
            ${shown.map((r) =>
              r.error
                ? html`<tr class="is-dim" title=${r.error}><td>${r.name}</td><td colspan="4">could not be read</td></tr>`
                : html`<tr
                    class="is-pickable ${r.episodes ? '' : 'is-dim'} ${r.entity === onChart ? 'is-on' : ''}"
                    title=${r.note ?? 'Show on the chart'}
                    @click=${() => this.showOnChart(r.entity)}
                  >
                    <td>${r.name}</td>
                    <td>${r.episodes ?? 0}</td>
                    <td>${r.episodes ? hoursWords(r.hours) : '-'}</td>
                    <td>${r.episodes ? hoursWords(r.longest_hours) : '-'}</td>
                    <td>${(r.counted ?? 0).toLocaleString()}</td>
                  </tr>`,
            )}
          </tbody>
        </table>
        ${quiet.length && !this.showQuiet
          ? html`<button type="button" class="argus-lmore" @click=${() => (this.showQuiet = true)}>
              and ${quiet.length} with none
            </button>`
          : nothing}
      </section>
      <p class="argus-lfaint">Run by the watcher's own code in ${Math.round(result.seconds)} s.</p>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-backtest-result': ArgusBacktestResultView;
  }
}
